import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Chip,
  Avatar,
  IconButton,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputBase,
  alpha,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  FiberManualRecord as FiberManualRecordIcon,
  NavigateNext as NavigateNextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit as EditIcon,
  SupportAgent as SupportAgentIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { GET_VISITORS } from "./graphql/queries";
import { gql } from "@apollo/client";

// Mutation to update visitor info
const UPDATE_VISITOR_INFO = gql`
  mutation UpdateVisitorInfo(
    $sessionId: String!
    $name: String
    $email: String
    $phone: String
    $company: String
  ) {
    updateVisitorInfo(
      sessionId: $sessionId
      name: $name
      email: $email
      phone: $phone
      company: $company
    ) {
      id
      sessionId
      name
      email
      phone
      company
    }
  }
`;

// Mutation to delete all visitors
const DELETE_ALL_VISITORS = gql`
  mutation DeleteAllVisitors($spaceId: ID!) {
    deleteAllVisitors(spaceId: $spaceId) {
      success
      count
      message
    }
  }
`;

// Styled components
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "20ch",
      "&:focus": {
        width: "30ch",
      },
    },
  },
}));

// Query to get all widgets for sidebar
const GET_ALL_WIDGETS = gql`
  query GetAllWidgets($spaceId: ID!) {
    getAllWidgets(spaceId: $spaceId) {
      id
      script_id
      business_details {
        display_name
      }
    }
  }
`;

// Helper functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Table head cells
const headCells = [
  { id: "nameOrId", label: "Name / ID", align: "left" },
  { id: "country", label: "Country", align: "left" },
  { id: "city", label: "City", align: "left" },
  { id: "browser", label: "Browser", align: "left" },
  { id: "pageViews", label: "Page Views", align: "right" },
  { id: "lastSeen", label: "Last Seen", align: "left" },
  { id: "status", label: "Status", align: "center" },
];

// Table head component
function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id !== "actions" && headCell.id !== "status" ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Visitor Card Component for Card View
const VisitorCard = ({ visitor, isSelected, onSelect }) => {
  return (
    <Card>
      <div style={{ position: "relative" }}>
        <div
          style={{
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "140px",
          }}
        >
          <Typography variant="h2" color="textSecondary">
            {visitor.name
              ? visitor.name.charAt(0).toUpperCase()
              : visitor.sessionId.charAt(0).toUpperCase()}
          </Typography>
        </div>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(e, visitor.id)}
          style={{ position: "absolute", top: "8px", left: "8px" }}
        />
        <Chip
          icon={<FiberManualRecordIcon />}
          label={visitor.isOnline ? "Online" : "Offline"}
          color={visitor.isOnline ? "success" : "default"}
          size="small"
          style={{ position: "absolute", top: "8px", right: "8px" }}
        />
      </div>
      <CardContent>
        <Box>
          <Typography variant="h6" gutterBottom noWrap>
            {visitor.name || visitor.sessionId}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <LanguageIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {visitor.city}, {visitor.country}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <ComputerIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {visitor.browser}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <VisibilityIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {visitor.totalPageViews} page views
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {visitor.lastSeen
                ? new Date(
                    typeof visitor.lastSeen === "string"
                      ? Number(visitor.lastSeen)
                      : visitor.lastSeen
                  ).toLocaleString()
                : "N/A"}
            </Typography>
          </Box>
          {visitor.chatSessionId && (
            <Chip
              icon={<SupportAgentIcon />}
              label="Support User"
              color="primary"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Visitor Details Modal
function VisitorDetailsModal({ visitor, open, onClose, refetchVisitors }) {
  const [editingField, setEditingField] = useState(null);
  const [localVisitor, setLocalVisitor] = useState(visitor);
  const [fieldValues, setFieldValues] = useState({
    name: visitor?.name || "",
    email: visitor?.email || "",
    phone: visitor?.phone || "",
    company: visitor?.company || "",
  });

  const [updateVisitorInfo] = useMutation(UPDATE_VISITOR_INFO, {
    onCompleted: (data) => {
      setEditingField(null);
      // Update local visitor state with the returned data
      if (data?.updateVisitorInfo) {
        setLocalVisitor(data.updateVisitorInfo);
        setFieldValues({
          name: data.updateVisitorInfo.name || "",
          email: data.updateVisitorInfo.email || "",
          phone: data.updateVisitorInfo.phone || "",
          company: data.updateVisitorInfo.company || "",
        });
      }
      if (refetchVisitors) refetchVisitors();
    },
    onError: (error) => {
      console.error("Error updating visitor:", error);
      alert("Failed to update visitor information");
    },
  });

  // Update field values when visitor prop changes
  useEffect(() => {
    if (visitor) {
      setLocalVisitor(visitor);
      setFieldValues({
        name: visitor.name || "",
        email: visitor.email || "",
        phone: visitor.phone || "",
        company: visitor.company || "",
      });
    }
  }, [visitor]);

  const handleFieldChange = (field, value) => {
    setFieldValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveField = async (field) => {
    if (fieldValues[field] === (localVisitor[field] || "")) {
      setEditingField(null);
      return;
    }

    try {
      await updateVisitorInfo({
        variables: {
          sessionId: localVisitor.sessionId,
          [field]: fieldValues[field] || null,
        },
      });
    } catch (error) {
      console.error("Error saving field:", error);
    }
  };

  const handleCancelEdit = (field) => {
    setFieldValues((prev) => ({ ...prev, [field]: localVisitor[field] || "" }));
    setEditingField(null);
  };

  if (!localVisitor) return null;

  // Debug logging for date issues
  console.log("🔍 [VISITOR-MODAL] Visitor data:", {
    firstSeen: visitor.firstSeen,
    lastSeen: visitor.lastSeen,
    firstSeenType: typeof visitor.firstSeen,
    lastSeenType: typeof visitor.lastSeen,
    pageViews: visitor.pageViews?.map((pv) => ({
      url: pv.url,
      viewedAt: pv.viewedAt,
      viewedAtType: typeof pv.viewedAt,
      duration: pv.duration,
    })),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">
              {localVisitor.name || "Anonymous Visitor"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Session ID: {localVisitor.sessionId}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          {/* Personal Information */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <PersonIcon sx={{ mr: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Personal Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Name Field */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Name
                  </Typography>
                  {editingField === "name" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        fullWidth
                        value={fieldValues.name}
                        onChange={(e) =>
                          handleFieldChange("name", e.target.value)
                        }
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSaveField("name")
                        }
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSaveField("name")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCancelEdit("name")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="body1"
                        onClick={() => setEditingField("name")}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          p: 0.5,
                          borderRadius: 1,
                          flex: 1,
                        }}
                      >
                        {localVisitor.name || (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Click to add...
                          </span>
                        )}
                      </Typography>
                      {localVisitor.name && (
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("name")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Email Field */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Email
                  </Typography>
                  {editingField === "email" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        value={fieldValues.email}
                        onChange={(e) =>
                          handleFieldChange("email", e.target.value)
                        }
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSaveField("email")
                        }
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSaveField("email")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCancelEdit("email")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="body1"
                        onClick={() => setEditingField("email")}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          p: 0.5,
                          borderRadius: 1,
                          flex: 1,
                        }}
                      >
                        {localVisitor.email || (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Click to add...
                          </span>
                        )}
                      </Typography>
                      {localVisitor.email && (
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("email")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Phone
                  </Typography>
                  {editingField === "phone" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        fullWidth
                        type="tel"
                        value={fieldValues.phone}
                        onChange={(e) =>
                          handleFieldChange("phone", e.target.value)
                        }
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSaveField("phone")
                        }
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSaveField("phone")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCancelEdit("phone")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="body1"
                        onClick={() => setEditingField("phone")}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          p: 0.5,
                          borderRadius: 1,
                          flex: 1,
                        }}
                      >
                        {localVisitor.phone || (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Click to add...
                          </span>
                        )}
                      </Typography>
                      {localVisitor.phone && (
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("phone")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Company Field */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Company
                  </Typography>
                  {editingField === "company" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        fullWidth
                        value={fieldValues.company}
                        onChange={(e) =>
                          handleFieldChange("company", e.target.value)
                        }
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSaveField("company")
                        }
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSaveField("company")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCancelEdit("company")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="body1"
                        onClick={() => setEditingField("company")}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          p: 0.5,
                          borderRadius: 1,
                          flex: 1,
                        }}
                      >
                        {localVisitor.company || (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Click to add...
                          </span>
                        )}
                      </Typography>
                      {localVisitor.company && (
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("company")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Location */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <LanguageIcon sx={{ mr: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Location
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Country
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.country}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    City
                  </Typography>
                  <Typography variant="body1">{localVisitor.city}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Region
                  </Typography>
                  <Typography variant="body1">{localVisitor.region}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Timezone
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.timezone}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1">{localVisitor.ip}</Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Device Information */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ComputerIcon sx={{ mr: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Device Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Browser
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.browser} {localVisitor.browserVersion}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Operating System
                  </Typography>
                  <Typography variant="body1">{localVisitor.os}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Device Type
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {localVisitor.device}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Screen Resolution
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.screenResolution}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Language
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.language}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Activity Timeline */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ScheduleIcon sx={{ mr: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Activity Timeline
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    First Seen
                  </Typography>
                  <Typography variant="body1">
                    {(() => {
                      console.log(
                        "🔍 [FIRST-SEEN] value:",
                        localVisitor.firstSeen,
                        "type:",
                        typeof localVisitor.firstSeen
                      );
                      if (!localVisitor.firstSeen) return "N/A";
                      // Convert timestamp string to number before passing to Date constructor
                      const timestamp =
                        typeof localVisitor.firstSeen === "string"
                          ? Number(localVisitor.firstSeen)
                          : localVisitor.firstSeen;
                      const date = new Date(timestamp);
                      const dateString = date.toLocaleString();
                      console.log(
                        "🔍 [FIRST-SEEN] Converted to:",
                        dateString,
                        "isValid:",
                        !isNaN(date.getTime())
                      );
                      return dateString;
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Last Seen
                  </Typography>
                  <Typography variant="body1">
                    {(() => {
                      console.log(
                        "🔍 [LAST-SEEN] value:",
                        localVisitor.lastSeen,
                        "type:",
                        typeof localVisitor.lastSeen
                      );
                      if (!localVisitor.lastSeen) return "N/A";
                      // Convert timestamp string to number before passing to Date constructor
                      const timestamp =
                        typeof localVisitor.lastSeen === "string"
                          ? Number(localVisitor.lastSeen)
                          : localVisitor.lastSeen;
                      const date = new Date(timestamp);
                      const dateString = date.toLocaleString();
                      console.log(
                        "🔍 [LAST-SEEN] Converted to:",
                        dateString,
                        "isValid:",
                        !isNaN(date.getTime())
                      );
                      return dateString;
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total Page Views
                  </Typography>
                  <Typography variant="body1">
                    {localVisitor.totalPageViews}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    icon={<FiberManualRecordIcon />}
                    label={localVisitor.isOnline ? "Online" : "Offline"}
                    color={localVisitor.isOnline ? "success" : "default"}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Page Views */}
              <Box mt={3}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Page Views History
                </Typography>
                <Box maxHeight={200} overflow="auto">
                  {visitor.pageViews && visitor.pageViews.length > 0 ? (
                    visitor.pageViews.map((pv, index) => (
                      <Box
                        key={index}
                        mb={2}
                        p={1}
                        bgcolor="grey.50"
                        borderRadius={1}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {pv.title || "Untitled"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {pv.url}
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mt={0.5}
                        >
                          <Typography variant="caption">
                            {(() => {
                              console.log(
                                "🔍 [PAGE-VIEW-DATE] viewedAt value:",
                                pv.viewedAt,
                                "type:",
                                typeof pv.viewedAt
                              );
                              if (!pv.viewedAt) return "N/A";
                              // Convert timestamp string to number before passing to Date constructor
                              const timestamp =
                                typeof pv.viewedAt === "string"
                                  ? Number(pv.viewedAt)
                                  : pv.viewedAt;
                              const date = new Date(timestamp);
                              const dateString = date.toLocaleString();
                              console.log(
                                "🔍 [PAGE-VIEW-DATE] Converted to:",
                                dateString,
                                "isValid:",
                                !isNaN(date.getTime())
                              );
                              return dateString;
                            })()}
                          </Typography>
                          {pv.duration && (
                            <Typography variant="caption" color="primary">
                              Duration: {Math.floor(pv.duration / 60)}m{" "}
                              {pv.duration % 60}s
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      No page views recorded
                    </Typography>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Main Visitors Component
export default function Visitors() {
  // Get spaceId from URL query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const spaceId = searchParams.get("spaceId");

  // State
  const [selectedWidget, setSelectedWidget] = useState(null); // null = "All Visitors"
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("lastSeen");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [visitorTypeFilter, setVisitorTypeFilter] = useState("all"); // 'all', 'support', 'browser'

  // GraphQL queries
  const { data: widgetsData, loading: widgetsLoading } = useQuery(
    GET_ALL_WIDGETS,
    {
      variables: { spaceId },
      skip: !spaceId,
    }
  );

  const {
    data: visitorsData,
    loading: visitorsLoading,
    error: visitorsError,
    refetch,
  } = useQuery(GET_VISITORS, {
    variables: {
      spaceId,
      widgetId: selectedWidget,
      limit: 100,
      offset: 0,
    },
    skip: !spaceId,
    pollInterval: 30000, // Poll every 30 seconds for real-time updates
  });

  // Delete all visitors mutation
  const [deleteAllVisitors] = useMutation(DELETE_ALL_VISITORS, {
    onCompleted: (data) => {
      if (data?.deleteAllVisitors?.success) {
        alert(data.deleteAllVisitors.message);
        refetch(); // Refresh the visitor list
      }
    },
    onError: (error) => {
      console.error("Error deleting visitors:", error);
      alert("Failed to delete visitors");
    },
  });

  // Get visitors and filter by search and type
  const visitors = visitorsData?.getVisitors || [];
  const filteredVisitors = visitors.filter((visitor) => {
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        visitor.country?.toLowerCase().includes(query) ||
        visitor.city?.toLowerCase().includes(query) ||
        visitor.browser?.toLowerCase().includes(query) ||
        visitor.name?.toLowerCase().includes(query) ||
        visitor.email?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Apply visitor type filter
    if (visitorTypeFilter === "support") {
      return visitor.chatSessionId != null;
    } else if (visitorTypeFilter === "browser") {
      return visitor.chatSessionId == null;
    }

    return true; // 'all' - show everything
  });

  // Get widgets
  const widgets = widgetsData?.getAllWidgets || [];

  // Handle sorting
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle selection
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredVisitors.map((v) => v.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle view details
  const handleViewDetails = (visitor) => {
    setSelectedVisitor(visitor);
    setDetailsModalOpen(true);
  };

  // Clear all visitors (for testing)
  const handleClearAllVisitors = () => {
    if (
      window.confirm(
        "Are you sure you want to delete ALL visitors? This action cannot be undone."
      )
    ) {
      deleteAllVisitors({
        variables: { spaceId },
      });
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Country",
      "City",
      "Browser",
      "Device",
      "OS",
      "Page Views",
      "Last Seen",
      "Status",
    ];
    const rows = filteredVisitors.map((v) => [
      v.country,
      v.city,
      v.browser,
      v.device,
      v.os,
      v.totalPageViews,
      v.lastSeen
        ? new Date(
            typeof v.lastSeen === "string" ? Number(v.lastSeen) : v.lastSeen
          ).toLocaleString()
        : "N/A",
      v.isOnline ? "Online" : "Offline",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `visitors_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Error state
  if (!spaceId) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Space ID is required. Please provide a spaceId parameter in the URL.
        </Alert>
      </Box>
    );
  }

  if (visitorsError) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Error loading visitors: {visitorsError.message}
        </Alert>
      </Box>
    );
  }

  // Sorted and paginated visitors
  const sortedVisitors = stableSort(
    filteredVisitors,
    getComparator(order, orderBy)
  );
  const paginatedVisitors = sortedVisitors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet title="Visitors" />

      <Box mb={4}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" />}
        >
          <MuiLink color="inherit" href="/widget/dashboard">
            Dashboard
          </MuiLink>
          <Typography color="textPrimary">Visitors</Typography>
        </Breadcrumbs>
        <Typography variant="h3" gutterBottom display="inline" mt={2}>
          Visitors
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Track and manage website visitors
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar - Widgets List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Widgets
            </Typography>
            {widgetsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                {/* All Visitors */}
                <Box
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: "pointer",
                    borderLeft: selectedWidget === null ? "3px solid" : "none",
                    borderColor: "primary.main",
                    bgcolor:
                      selectedWidget === null
                        ? "action.selected"
                        : "transparent",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                  onClick={() => setSelectedWidget(null)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selectedWidget === null ? "bold" : "normal"}
                  >
                    All Visitors
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {visitors.length} visitors
                  </Typography>
                </Box>

                {/* Individual Widgets */}
                {widgets.map((widget) => (
                  <Box
                    key={widget.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: "pointer",
                      borderLeft:
                        selectedWidget === widget.id ? "3px solid" : "none",
                      borderColor: "primary.main",
                      bgcolor:
                        selectedWidget === widget.id
                          ? "action.selected"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => setSelectedWidget(widget.id)}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={
                        selectedWidget === widget.id ? "bold" : "normal"
                      }
                    >
                      {widget.business_details?.display_name ||
                        widget.script_id}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {visitors.filter((v) => v.widgetId === widget.id).length}{" "}
                      visitors
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Main Content - Visitors Table */}
        <Grid item xs={12} md={9}>
          <Paper>
            {/* Toolbar */}
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(selected.length > 0 && {
                  bgcolor: (theme) =>
                    alpha(
                      theme.palette.primary.main,
                      theme.palette.action.activatedOpacity
                    ),
                }),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "70px",
                borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
              }}
            >
              <Box>
                {selected.length > 0 ? (
                  <Typography
                    color="inherit"
                    variant="subtitle1"
                    sx={{ fontWeight: 500 }}
                  >
                    {selected.length} selected
                  </Typography>
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Search>
                      <SearchIconWrapper>
                        <SearchIcon />
                      </SearchIconWrapper>
                      <StyledInputBase
                        placeholder="Search visitors..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Search>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={visitorTypeFilter}
                        onChange={(e) => setVisitorTypeFilter(e.target.value)}
                        displayEmpty
                        sx={{ bgcolor: "background.paper" }}
                      >
                        <MenuItem value="all">All Visitors</MenuItem>
                        <MenuItem value="support">Support Users</MenuItem>
                        <MenuItem value="browser">Browsers Only</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary">
                      {filteredVisitors.length} visitors
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box>
                {selected.length > 0 ? (
                  <Box display="flex" gap={1}>
                    {selected.length === 1 && (
                      <Tooltip title="View Details">
                        <IconButton
                          aria-label="View"
                          size="large"
                          onClick={() => {
                            const visitor = filteredVisitors.find(
                              (v) => v.id === selected[0]
                            );
                            if (visitor) handleViewDetails(visitor);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                      <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export CSV">
                      <IconButton onClick={handleExportCSV}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear All Visitors (Testing)">
                      <IconButton
                        onClick={handleClearAllVisitors}
                        color="error"
                      >
                        <DeleteSweepIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        viewMode === "list"
                          ? "Change to Card View"
                          : "Change to List View"
                      }
                    >
                      <IconButton
                        color="primary"
                        onClick={() =>
                          setViewMode(viewMode === "list" ? "grid" : "list")
                        }
                        size="medium"
                        sx={{
                          borderRadius: "8px",
                          p: 1,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, 0.08),
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.12),
                          },
                        }}
                      >
                        {viewMode === "list" ? (
                          <ViewModuleIcon />
                        ) : (
                          <ViewListIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Toolbar>

            {/* Table */}
            {visitorsLoading ? (
              <Box display="flex" justifyContent="center" p={8}>
                <CircularProgress />
              </Box>
            ) : filteredVisitors.length === 0 ? (
              <Box p={8} textAlign="center">
                <Typography variant="h6" color="textSecondary">
                  No visitors found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Visitors will appear here once your widget starts tracking
                </Typography>
              </Box>
            ) : viewMode === "grid" ? (
              <Box p={3}>
                <Grid container spacing={3}>
                  {paginatedVisitors.map((visitor) => {
                    const isItemSelected = isSelected(visitor.id);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={visitor.id}>
                        <VisitorCard
                          visitor={visitor}
                          isSelected={isItemSelected}
                          onSelect={handleClick}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredVisitors.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <EnhancedTableHead
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={filteredVisitors.length}
                    />
                    <TableBody>
                      {paginatedVisitors.map((visitor, index) => {
                        const isItemSelected = isSelected(visitor.id);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={visitor.id}
                            selected={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                                onClick={(event) =>
                                  handleClick(event, visitor.id)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip
                                  title={
                                    visitor.chatSessionId
                                      ? "Support User - Requested agent help"
                                      : "Browser - Just visiting"
                                  }
                                  arrow
                                >
                                  {visitor.chatSessionId ? (
                                    <SupportAgentIcon
                                      fontSize="small"
                                      color="primary"
                                    />
                                  ) : (
                                    <PersonIcon
                                      fontSize="small"
                                      sx={{ color: "text.secondary" }}
                                    />
                                  )}
                                </Tooltip>
                                <Tooltip title={visitor.sessionId} arrow>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ maxWidth: 160 }}
                                  >
                                    {visitor.name || visitor.sessionId}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>{visitor.country}</TableCell>
                            <TableCell>{visitor.city}</TableCell>
                            <TableCell>{visitor.browser}</TableCell>
                            <TableCell align="right">
                              {visitor.totalPageViews}
                            </TableCell>
                            <TableCell>
                              {visitor.lastSeen
                                ? new Date(
                                    typeof visitor.lastSeen === "string"
                                      ? Number(visitor.lastSeen)
                                      : visitor.lastSeen
                                  ).toLocaleString()
                                : "N/A"}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<FiberManualRecordIcon />}
                                label={visitor.isOnline ? "Online" : "Offline"}
                                color={visitor.isOnline ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredVisitors.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Visitor Details Modal */}
      <VisitorDetailsModal
        visitor={selectedVisitor}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        refetchVisitors={refetch}
      />
    </>
  );
}
